import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GAME_CONFIG } from '../config/gameConfig';

const VirtualJoystick = ({ onDirectionChange, disabled }) => {
  const joystickRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const touchIdRef = useRef(null);

  const joystickSize = GAME_CONFIG.joystickSize;
  const maxDistance = joystickSize / 2 - 10;
  const knobSize = joystickSize / 3;

  useEffect(() => {
    const joystick = joystickRef.current;
    if (!joystick || disabled) return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      touchIdRef.current = touch.identifier;
      setIsActive(true);
      updatePosition(touch);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (touchIdRef.current === null) return;

      const touch = Array.from(e.touches).find(
        t => t.identifier === touchIdRef.current
      );
      if (touch) {
        updatePosition(touch);
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touches = Array.from(e.changedTouches);
      const endedTouch = touches.find(t => t.identifier === touchIdRef.current);

      if (endedTouch) {
        touchIdRef.current = null;
        setIsActive(false);
        setPosition({ x: 0, y: 0 });
        onDirectionChange({ x: 0, y: 0 });
      }
    };

    const updatePosition = (touch) => {
      const rect = joystick.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      let deltaX = touch.clientX - centerX;
      let deltaY = touch.clientY - centerY;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > maxDistance) {
        const angle = Math.atan2(deltaY, deltaX);
        deltaX = Math.cos(angle) * maxDistance;
        deltaY = Math.sin(angle) * maxDistance;
      }

      setPosition({ x: deltaX, y: deltaY });

      // Send normalized direction vector for smooth diagonal movement
      if (distance > 10) {
        const normalizedX = deltaX / maxDistance;
        const normalizedY = deltaY / maxDistance;
        onDirectionChange({ x: normalizedX, y: normalizedY });
      } else {
        onDirectionChange({ x: 0, y: 0 });
      }
    };

    joystick.addEventListener('touchstart', handleTouchStart, { passive: false });
    joystick.addEventListener('touchmove', handleTouchMove, { passive: false });
    joystick.addEventListener('touchend', handleTouchEnd, { passive: false });
    joystick.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      joystick.removeEventListener('touchstart', handleTouchStart);
      joystick.removeEventListener('touchmove', handleTouchMove);
      joystick.removeEventListener('touchend', handleTouchEnd);
      joystick.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [disabled, onDirectionChange, maxDistance]);

  return (
    <div className="flex flex-col items-center">
      <div
        ref={joystickRef}
        className={`relative rounded-full bg-black/40 backdrop-blur-md border-2 transition-colors duration-200 ${
          isActive ? 'border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.3)]' : 'border-gray-600/50'
        }`}
        style={{
          width: `${joystickSize}px`,
          height: `${joystickSize}px`,
          touchAction: 'none'
        }}
      >
        {/* Inner indicators */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30">
          <div className="w-1.5 h-1.5 bg-white rounded-full absolute top-3" />
          <div className="w-1.5 h-1.5 bg-white rounded-full absolute bottom-3" />
          <div className="w-1.5 h-1.5 bg-white rounded-full absolute left-3" />
          <div className="w-1.5 h-1.5 bg-white rounded-full absolute right-3" />
        </div>

        {/* Center circle (default position indicator) */}
        <div
          className="absolute top-1/2 left-1/2 rounded-full bg-gray-700/50 -translate-x-1/2 -translate-y-1/2 border border-gray-600/30"
          style={{
            width: `${knobSize}px`,
            height: `${knobSize}px`
          }}
        />

        {/* Knob */}
        <motion.div
          className={`absolute rounded-full shadow-lg z-10 ${isActive ? 'bg-cyan-500' : 'bg-gray-500'}`}
          style={{
            width: `${knobSize}px`,
            height: `${knobSize}px`,
            top: '50%',
            left: '50%',
            marginTop: `-${knobSize / 2}px`,
            marginLeft: `-${knobSize / 2}px`
          }}
          animate={{
            x: position.x,
            y: position.y
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }}
        />
      </div>

      <div className="mt-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
        Move
      </div>
    </div>
  );
};

export default VirtualJoystick;